import { useRef, useState } from "react";
import { Trash2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Field, Input, Card, ChoiceInput } from "../kit";
import { CUP_TYPES, CUP_SIZES, PRINT_SIDES, rupiah } from "../../lib/constants";
import { useApi } from "../../lib/api";

export const NEW_ITEM = { cup_type: "", cup_size: "", qty: "", colors: 1, print_side: "", unit_price: "", files: [] };

function Uploader({ files, onChange, idx }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const list = [...e.target.files];
    if (!list.length) return;
    setBusy(true);
    try {
      const out = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        out.push((await api.post("/files/upload", fd)).data);
      }
      onChange([...files, ...out]);
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <div>
      <input ref={ref} type="file" multiple accept="image/*,application/pdf,.ai,.cdr,.psd" className="hidden" onChange={upload} data-testid={`item-${idx}-file-input`} />
      <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid={`item-${idx}-upload-btn`}
        className="w-full min-h-[48px] rounded-xl border-2 border-dashed border-slate-300 text-slate-600 font-semibold inline-flex items-center justify-center gap-2 hover:border-sky-400 hover:text-sky-600">
        <Paperclip className="w-5 h-5" /> {busy ? "Mengunggah..." : "Unggah desain / foto"}
      </button>
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <span key={f.path} className="inline-flex items-center gap-1 pl-3 pr-1 h-8 rounded-full bg-sky-50 text-sky-800 text-xs font-semibold border border-sky-200">
              <span className="max-w-[140px] truncate">{f.name}</span>
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} className="w-6 h-6 grid place-items-center"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function ItemEditor({ item, idx, onChange, onRemove, cupTypes, cupSizes }) {
  const set = (k) => (e) => onChange({ ...item, [k]: e.target.value });
  const setV = (k) => (v) => onChange({ ...item, [k]: v });
  const { data: opt } = useApi("/settings/options", 0);
  const types = [...new Set([...(opt?.cup_types || CUP_TYPES), ...cupTypes])], sizes = [...new Set([...(opt?.cup_sizes || CUP_SIZES), ...cupSizes])];
  const sides = opt?.print_sides || PRINT_SIDES;
  const subtotal = (Number(item.qty) || 0) * (Number(item.unit_price) || 0);
  return (
    <Card className="space-y-3 relative" data-testid={`order-item-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-black text-slate-400">ITEM #{idx + 1}</span>
        {onRemove && <button type="button" onClick={onRemove} className="w-10 h-10 grid place-items-center rounded-xl text-rose-600 hover:bg-rose-50" data-testid={`item-${idx}-remove-btn`}><Trash2 className="w-5 h-5" /></button>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Jenis cup"><ChoiceInput options={types} required value={item.cup_type} onChange={setV("cup_type")} testid={`item-${idx}-cup-type`} placeholder="Contoh: Plastik PP" /></Field>
        <Field label="Ukuran"><ChoiceInput options={sizes} required value={item.cup_size} onChange={setV("cup_size")} testid={`item-${idx}-cup-size`} placeholder="Contoh: 8 oz" /></Field>
        <Field label="Jumlah (pcs)"><Input type="number" inputMode="numeric" min="1" required value={item.qty} onChange={set("qty")} data-testid={`item-${idx}-qty`} /></Field>
        <Field label="Jumlah warna"><Input type="number" inputMode="numeric" min="1" value={item.colors} onChange={set("colors")} data-testid={`item-${idx}-colors`} /></Field>
        <Field label="Posisi / sisi cetak"><ChoiceInput options={sides} value={item.print_side} onChange={setV("print_side")} testid={`item-${idx}-print-side`} placeholder="Contoh: Kiri-kanan" /></Field>
        <Field label="Harga satuan (Rp)"><Input type="number" inputMode="decimal" min="0" value={item.unit_price} onChange={set("unit_price")} data-testid={`item-${idx}-unit-price`} /></Field>
      </div>
      <Uploader idx={idx} files={item.files} onChange={(files) => onChange({ ...item, files })} />
      <div className="flex justify-between items-center pt-2 border-t border-slate-100">
        <span className="text-sm text-slate-500">Subtotal</span>
        <span className="font-mono font-bold text-lg" data-testid={`item-${idx}-subtotal`}>{rupiah(subtotal)}</span>
      </div>
    </Card>
  );
}
